import React, { useEffect, useMemo, useState } from 'react';

import { Grid, Input } from '@mui/material';
import { FlexySelect, OptionsType } from '@europrocurement/flexy-components';

import { Box } from '@mui/system';
// eslint-disable-next-line import/no-cycle
import { customItemJsonProps } from '../customItemRenderer';
import CategoriesActivitesJson from './CategoriesActivites.json';

interface SousActivite {
    code: string;
    nom: string;
    code_final: string;
}

interface Activite {
    code: string;
    nom: string;
    sous_activites: SousActivite[];
}

interface SousSecteur {
    code: string;
    nom: string;
    activites: Activite[];
}

interface Secteur {
    code_sectoriel: string;
    secteur: string;
    sous_secteurs: SousSecteur[];
}

const CategoriesActivites: React.FunctionComponent<customItemJsonProps> = function ({
    formContext,
    props,
}) {
    formContext.watch(props.name);

    const defaultValue = formContext.getValues(props.name);

    const secteurs: Secteur[] = CategoriesActivitesJson;

    const [innerValue, setInnerValue] = useState<string>('');

    const [secteurId, setSecteurId] = useState<{ value: string; label: string } | null>(null);
    const [sousSecteurId, setSousSecteurId] = useState<{ value: string; label: string } | null>(
        null,
    );
    const [activiteId, setActiviteId] = useState<{ value: string; label: string } | null>(null);
    const [sousActiviteId, setSousActiviteId] = useState<{ value: string; label: string } | null>(
        null,
    );

    const secteurOptions: OptionsType = useMemo(
        () =>
            secteurs.map((secteur) => ({
                value: secteur.code_sectoriel,
                label: secteur.secteur,
            })),
        [secteurs],
    );

    const sousSecteurOptions: OptionsType = useMemo(() => {
        if (!secteurId) return [];
        const secteur = secteurs.find((s) => s.code_sectoriel === secteurId.value);
        if (!secteur) return [];
        if (secteur.sous_secteurs.length > 0 && secteur.sous_secteurs[0].code === '00') {
            return [];
        }
        return secteur.sous_secteurs.map((ss) => ({
            value: ss.code,
            label: ss.nom,
        }));
    }, [secteurId, secteurs]);

    const activiteOptions: OptionsType = useMemo(() => {
        if (!secteurId || !sousSecteurId) return [];
        const secteur = secteurs.find((s) => s.code_sectoriel === secteurId.value);
        if (!secteur) return [];
        const sousSecteur = secteur.sous_secteurs.find((ss) => ss.code === sousSecteurId.value);
        if (!sousSecteur) return [];
        if (sousSecteur.activites.length > 0 && sousSecteur.activites[0].code === '00') {
            return [];
        }
        return sousSecteur.activites.map((a) => ({
            value: a.code,
            label: a.nom,
        }));
    }, [secteurId, sousSecteurId, secteurs]);

    const sousActiviteOptions: OptionsType = useMemo(() => {
        if (!secteurId || !sousSecteurId || !activiteId) return [];
        const secteur = secteurs.find((s) => s.code_sectoriel === secteurId.value);
        if (!secteur) return [];
        const sousSecteur = secteur.sous_secteurs.find((ss) => ss.code === sousSecteurId.value);
        if (!sousSecteur) return [];
        const activite = sousSecteur.activites.find((a) => a.code === activiteId.value);
        if (!activite) return [];
        if (activite.sous_activites.length > 0 && activite.sous_activites[0].code === '00') {
            return [];
        }
        return activite.sous_activites.map((sa) => ({
            value: sa.code,
            label: sa.nom,
        }));
    }, [secteurId, sousSecteurId, activiteId, secteurs]);

    const setValueInForm = (value: string) => {
        setInnerValue(value);
        formContext.setValue(props.name, value);
    };

    useEffect(() => {
        if (!defaultValue || defaultValue.length !== 8) return;

        const secteurCode = defaultValue.substring(0, 2);
        const sousSecteurCode = defaultValue.substring(2, 4);
        const activiteCode = defaultValue.substring(4, 6);
        const sousActiviteCode = defaultValue.substring(6, 8);

        const secteur = secteurs.find((s) => s.code_sectoriel === secteurCode);
        if (secteur) {
            setSecteurId({ value: secteur.code_sectoriel, label: secteur.secteur });

            const sousSecteur = secteur.sous_secteurs.find((ss) => ss.code === sousSecteurCode);
            if (sousSecteur) {
                setSousSecteurId({ value: sousSecteur.code, label: sousSecteur.nom });

                const activite = sousSecteur.activites.find((a) => a.code === activiteCode);
                if (activite) {
                    setActiviteId({ value: activite.code, label: activite.nom });

                    const sousActivite = activite.sous_activites.find(
                        (sa) => sa.code === sousActiviteCode,
                    );
                    if (sousActivite) {
                        setSousActiviteId({ value: sousActivite.code, label: sousActivite.nom });
                    }
                }
            }
        }

        setValueInForm(defaultValue);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultValue]);

    const handleSecteurChange = (value: { value: string; label: string }) => {
        setSecteurId(value);
        setSousSecteurId(null);
        setActiviteId(null);
        setSousActiviteId(null);
        setValueInForm(`${value.value}000000`);
    };

    const handleSousSecteurChange = (value: { value: string; label: string }) => {
        setSousSecteurId(value);
        setActiviteId(null);
        setSousActiviteId(null);
        if (secteurId) setValueInForm(`${secteurId.value + value.value}0000`);
    };

    const handleActiviteChange = (value: { value: string; label: string }) => {
        setActiviteId(value);
        setSousActiviteId(null);
        if (secteurId && sousSecteurId)
            setValueInForm(`${secteurId.value + sousSecteurId.value + value.value}00`);
    };

    const handleSousActiviteChange = (value: { value: string; label: string }) => {
        setSousActiviteId(value);
        if (secteurId && sousSecteurId && activiteId)
            setValueInForm(
                `${secteurId.value + sousSecteurId.value + activiteId.value + value.value}`,
            );
    };

    return (
        <Box>
            <Grid
                container
                spacing={{ xs: 0, md: 2 }}
                sx={{ paddingTop: '20px' }}
            >
                <Grid
                    item
                    xs={12}
                    md={6}
                    lg={6}
                    xl={6}
                    sx={{
                        lineHeight: '40px',
                    }}
                >
                    <label>Catégorisation 1 de l&apos;activité</label>
                    <FlexySelect
                        name="cat_niv1"
                        options={secteurOptions}
                        onChange={handleSecteurChange}
                        value={secteurId}
                    />
                </Grid>
                <Grid
                    item
                    xs={12}
                    md={6}
                    lg={6}
                    xl={6}
                    sx={{
                        lineHeight: '40px',
                    }}
                >
                    {secteurId && sousSecteurOptions.length > 0 && (
                        <>
                            <label>Catégorisation 2 de l&apos;activité</label>
                            <FlexySelect
                                name="cat_niv2"
                                options={sousSecteurOptions}
                                onChange={handleSousSecteurChange}
                                value={sousSecteurId}
                            />
                        </>
                    )}
                </Grid>
                <Grid
                    item
                    xs={12}
                    md={6}
                    lg={6}
                    xl={6}
                    sx={{
                        lineHeight: '40px',
                    }}
                >
                    {sousSecteurId && activiteOptions.length > 0 && (
                        <>
                            <label>Catégorisation 3 de l&apos;activité</label>
                            <FlexySelect
                                name="cat_niv3"
                                options={activiteOptions}
                                onChange={handleActiviteChange}
                                value={activiteId}
                            />
                        </>
                    )}
                </Grid>
                <Grid
                    item
                    xs={12}
                    md={6}
                    lg={6}
                    xl={6}
                    sx={{
                        lineHeight: '40px',
                    }}
                >
                    {activiteId && sousActiviteOptions.length > 0 && (
                        <>
                            <label>Catégorisation 4 de l&apos;activité</label>
                            <FlexySelect
                                name="cat_niv4"
                                options={sousActiviteOptions}
                                onChange={handleSousActiviteChange}
                                value={sousActiviteId}
                            />
                        </>
                    )}
                </Grid>
            </Grid>
            <Input
                value={innerValue}
                {...props}
                type="hidden"
            />
        </Box>
    );
};

export default CategoriesActivites;
